import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocale } from '../contexts/LocaleContext';
import { priceCartForPreview } from '../lib/pricing';
import * as types from '../types';
import useHardwareFirstCheckout from './useHardwareFirstCheckout';

const PDP_PREVIEW_CART_ITEM_ID = 'hardware-first-pdp-preview';

/**
 * Server-resolved hardware-first price for a PDP: prices a synthetic one-unit cart through the
 * pricing endpoint and returns its strikethrough data. The synthetic cart carries the code the
 * shopper has applied, so a promotion they have not taken yet quotes list price. `couponCodeOverride`
 * prices with that code instead, for quoting an offer the shopper has not applied yet beside the
 * price they would pay today. The client never computes arm prices. Undefined while loading, when
 * not enrolled, or when the pricing call fails (the PDP then omits the price row).
 */
export default function useHardwareFirstPdpPricing(
  sku: string | undefined,
  couponCodeOverride?: string,
): types.HardwareDiscountPreview | undefined {
  const enrolled = useHardwareFirstCheckout();
  const { currency } = useLocale();
  const cartCouponCode = useSelector((state: types.AppState) => state.storeShop.cart.couponCode);
  const couponCode = couponCodeOverride ?? cartCouponCode;
  const [discount, setDiscount] = useState<types.HardwareDiscountPreview | undefined>(undefined);

  useEffect(() => {
    if (!enrolled || !sku) {
      setDiscount(undefined);
      return;
    }

    let cancelled = false;
    const cart: types.StoreCart = {
      cartItems: {
        [PDP_PREVIEW_CART_ITEM_ID]: {
          cartItemId: PDP_PREVIEW_CART_ITEM_ID,
          lineItem: { lineItemId: PDP_PREVIEW_CART_ITEM_ID, sku },
          quantity: 1,
        },
      },
      currency,
      couponCode,
    };

    priceCartForPreview(cart)
      .then((pricing) => {
        if (!cancelled) {
          setDiscount(pricing.hardwareDiscounts?.[PDP_PREVIEW_CART_ITEM_ID]);
        }
      })
      // already reported inside priceCartForPreview; the PDP degrades to no price row
      .catch(() => {
        if (!cancelled) {
          setDiscount(undefined);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enrolled, sku, currency, couponCode]);

  return discount;
}
